import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import moment from 'moment';
import { Box, LinearProgress } from '@mui/material';

import useNotification from '../../../../../core/hooks/use_notification';
import { getProductsAvailabilityAction, getProductsAvailabilityState } from '../../../../../features/reservation/get_products_availability';
import { getFoodPartnersForReservationNotificationData, getHubsNotificationData, getPopsNotificationData, getProductsAvailabilityNotificationData } from '../../../../../core/utils/notification_utils';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT } from '../../../../../core/constants';
import CalendarFilters from './calendar_filters';
import CalendarList from './calendar_list';
import { getFoodPartnersForReservationAction, getFoodPartnersForReservationState } from '../../../../../features/food_partner/get_food_partners_for_reservation';
import { getAllHubsAction, getAllHubsState } from "../../../../../features/pop/get_all_hubs";
import { getAllPopsAction, getAllPopsState } from "../../../../../features/pop/get_all_pops";

const initialQueryParams = {
    languageCode: 'PL',
    from: moment().add(1, 'day'),
    to: moment().add(1, 'day').endOf('week').add(1, 'week')
};

const Calendar = () => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const getProductsAvailability = useSelector(getProductsAvailabilityState);
    const getFoodPartnersForReservation = useSelector(getFoodPartnersForReservationState);
    const getAllHubs = useSelector(getAllHubsState);
    const getAllPops = useSelector(getAllPopsState);

    const [queryParams, setQueryParams] = useState(initialQueryParams);

    const refreshCalendar = useCallback(() => {
        const params = {
            ...queryParams,
            from: queryParams?.from?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT),
            to: queryParams?.to?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT)
        };

        dispatch(getProductsAvailabilityAction(params))
            .then(response => showErrorMessage(getProductsAvailabilityNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    useEffect(() => {
        refreshCalendar();
    }, [refreshCalendar]);

    useEffect(() => {
        dispatch(getFoodPartnersForReservationAction())
            .then(response => showErrorMessage(getFoodPartnersForReservationNotificationData(response)));
        dispatch(getAllHubsAction())
            .then(response => showErrorMessage(getHubsNotificationData(response)));
        dispatch(getAllPopsAction())
            .then(response => showErrorMessage(getPopsNotificationData(response)));
    }, [dispatch, showErrorMessage]);

    const isLoading = getProductsAvailability?.loading || getFoodPartnersForReservation?.loading || getAllHubs?.loading || getAllPops?.loading;
    const isReady = getProductsAvailability?.data && getFoodPartnersForReservation?.data && getAllHubs?.data && getAllPops?.data;

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', gap: 3, position: 'relative', pt: 1 } }>

            <Box sx={ { position: 'absolute', top: -8, left: 0, width: '100%', zIndex: 10, height: 4 } }>
                { isLoading && <LinearProgress/> }
            </Box>

            { isReady && (
                <Box sx={ {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    opacity: isLoading ? 0.4 : 1,
                    pointerEvents: isLoading ? 'none' : 'auto',
                    transition: 'opacity 0.2s ease-in-out',
                    width: '100%'
                } }>
                    <CalendarFilters
                        from={ queryParams.from }
                        onChangeTimeFrom={ from => setQueryParams(prevState => ({ ...prevState, from })) }
                        to={ queryParams.to }
                        onChangeTimeTo={ to => setQueryParams(prevState => ({ ...prevState, to })) }
                        refreshCalendar={ refreshCalendar }
                    />
                    <CalendarList
                        from={ queryParams.from }
                        to={ queryParams.to }
                        refreshCalendar={ refreshCalendar }
                    />
                </Box>
            ) }
        </Box>
    );
};

export default Calendar;